/**
 * UI Health Monitor
 * Tracks UI render health and detects blank screen conditions
 *
 * Multiple signals used to detect unhealthy UI:
 * 1. Last render timestamp - updated by components on successful paint
 * 2. Stable UI elements - header, clock, footer presence + root container height
 * 3. Global JS error hooks - window.onerror + unhandled promise rejections
 */

// Health state
let lastRenderTimestamp = Date.now();
let lastRenderComponent = null;
let jsErrorCount = 0;
let lastJsError = null;
let unhandledRejectionCount = 0;
let lastUnhandledRejection = null;
let isUIHealthy = true;
let healthCheckCallbacks = [];

// Thresholds
const RENDER_STALE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes without render = stale
const ERROR_THRESHOLD = 3; // 3+ JS errors in short period = unhealthy
const ERROR_WINDOW_MS = 60 * 1000; // Error counting window (1 minute)

// Error tracking with timestamps for windowed counting
let recentErrors = [];

/**
 * Mark a successful render from a component
 * Call this from useEffect in main display components
 * @param {string} componentName - Name of component that rendered (e.g., 'Arrival', 'Departure')
 */
export function markRenderSuccess(componentName) {
  lastRenderTimestamp = Date.now();
  lastRenderComponent = componentName;

  // If we were unhealthy due to stale renders, check if we're healthy now
  if (!isUIHealthy) {
    checkUIHealth();
  }
}

/**
 * Get time since last successful render in seconds
 */
export function getTimeSinceLastRender() {
  return Math.floor((Date.now() - lastRenderTimestamp) / 1000);
}

/**
 * Check if stable UI elements are present and visible
 * Looks for header, clock, footer, and checks root container has height
 */
function checkStableUIElements() {
  try {
    // Check for root container with content
    const root = document.getElementById('root');
    if (!root || root.offsetHeight < 100) {
      return { healthy: false, reason: 'Root container empty or too small' };
    }

    // Check for FIDS container (main layout wrapper)
    const fidsContainer = document.querySelector('.fids_container');
    if (!fidsContainer || fidsContainer.offsetHeight < 100) {
      return { healthy: false, reason: 'FIDS container missing or collapsed' };
    }

    // Check for header (yellow bar with time)
    const header = document.querySelector('.fids_container > div:first-child');
    if (!header || header.offsetHeight < 40) {
      return { healthy: false, reason: 'Header missing or collapsed' };
    }

    // Check for clock element (updates every second - good liveness indicator)
    const clock = document.querySelector('.time');
    if (!clock || !clock.textContent || !/\d{2}:\d{2}/.test(clock.textContent)) {
      return { healthy: false, reason: 'Clock not rendering' };
    }

    // Check for table structure
    const table = document.querySelector('table');
    if (!table) {
      return { healthy: false, reason: 'Flight table missing' };
    }

    // Check for actual flight data rows (not just ghost/empty rows)
    // Data rows have class 'data-row-even' or 'data-row-odd'
    // Ghost/empty rows have class 'ghost-row-even' or 'ghost-row-odd'
    const dataRows = table.querySelectorAll('tr[class*="data-row"]');
    if (dataRows.length === 0) {
      return { healthy: false, reason: 'Flight table is empty - no flights displayed' };
    }

    return { healthy: true, reason: null };
  } catch (error) {
    return { healthy: false, reason: `DOM check error: ${error.message}` };
  }
}

/**
 * Check overall UI health
 * Returns health status with details
 */
export function checkUIHealth() {
  const now = Date.now();
  const timeSinceRender = now - lastRenderTimestamp;
  const renderStale = timeSinceRender > RENDER_STALE_THRESHOLD_MS;

  // Clean up old errors outside the window
  recentErrors = recentErrors.filter(ts => now - ts < ERROR_WINDOW_MS);
  const recentErrorCount = recentErrors.length;
  const tooManyErrors = recentErrorCount >= ERROR_THRESHOLD;

  // Check DOM elements
  const domCheck = checkStableUIElements();

  // Determine overall health
  const wasHealthy = isUIHealthy;

  if (tooManyErrors) {
    isUIHealthy = false;
  } else if (!domCheck.healthy) {
    isUIHealthy = false;
  } else if (renderStale) {
    isUIHealthy = false;
  } else {
    isUIHealthy = true;
  }

  const status = {
    healthy: isUIHealthy,
    lastRenderTimestamp,
    lastRenderComponent,
    timeSinceRenderSeconds: Math.floor(timeSinceRender / 1000),
    renderStale,
    recentJsErrors: recentErrorCount,
    tooManyErrors,
    domHealthy: domCheck.healthy,
    domReason: domCheck.reason,
    lastJsError,
    lastUnhandledRejection
  };

  // Log state changes
  if (wasHealthy && !isUIHealthy) {
    console.error('🚨 UI Health: UNHEALTHY', status);
  } else if (!wasHealthy && isUIHealthy) {
    console.log('✅ UI Health: Recovered', status);
  }

  // Notify callbacks
  if (wasHealthy !== isUIHealthy) {
    healthCheckCallbacks.forEach(cb => {
      try {
        cb(status);
      } catch (e) {
        console.error('Health callback error:', e);
      }
    });
  }

  return status;
}

/**
 * Get current UI health status (cached, doesn't recheck DOM)
 */
export function getUIHealthStatus() {
  return {
    healthy: isUIHealthy,
    lastRenderTimestamp,
    lastRenderComponent,
    timeSinceRenderSeconds: getTimeSinceLastRender(),
    recentJsErrors: recentErrors.length,
    lastJsError,
    lastUnhandledRejection
  };
}

/**
 * Register callback for health state changes
 * @param {Function} callback - Called with health status when state changes
 */
export function onHealthChange(callback) {
  healthCheckCallbacks.push(callback);
  return () => {
    healthCheckCallbacks = healthCheckCallbacks.filter(cb => cb !== callback);
  };
}

/**
 * Initialize global error handlers
 * Call once at app startup
 */
export function initializeErrorHandlers() {
  // Global JS error handler
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    jsErrorCount++;
    recentErrors.push(Date.now());
    lastJsError = {
      message,
      source,
      lineno,
      colno,
      timestamp: new Date().toISOString()
    };

    console.error('🚨 JS Error caught by UI health monitor:', {
      message,
      source: `${source}:${lineno}:${colno}`,
      totalErrors: jsErrorCount,
      recentErrors: recentErrors.length
    });

    // Check health after error
    checkUIHealth();

    // Call original handler if exists
    if (originalOnError) {
      return originalOnError.apply(this, arguments);
    }
    return false;
  };

  // Unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    unhandledRejectionCount++;
    recentErrors.push(Date.now());
    lastUnhandledRejection = {
      reason: event.reason?.message || event.reason || 'Unknown',
      timestamp: new Date().toISOString()
    };

    console.error('🚨 Unhandled promise rejection caught by UI health monitor:', {
      reason: lastUnhandledRejection.reason,
      totalRejections: unhandledRejectionCount,
      recentErrors: recentErrors.length
    });

    // Check health after error
    checkUIHealth();
  });

  console.log('🏥 UI Health Monitor: Error handlers initialized');
}

/**
 * Start periodic UI health checks
 * @param {number} intervalMs - Check interval in milliseconds (default 30 seconds)
 * @returns {Function} Cleanup function to stop checks
 */
export function startPeriodicHealthChecks(intervalMs = 30000) {
  console.log(`🏥 UI Health Monitor: Starting periodic checks (every ${intervalMs / 1000}s)`);

  const intervalId = setInterval(() => {
    const status = checkUIHealth();

    // Log periodic status (verbose for debugging)
    if (!status.healthy) {
      console.warn('🏥 UI Health Check: UNHEALTHY', {
        timeSinceRender: `${status.timeSinceRenderSeconds}s`,
        renderStale: status.renderStale,
        domHealthy: status.domHealthy,
        domReason: status.domReason,
        recentErrors: status.recentJsErrors
      });
    }
  }, intervalMs);

  return () => {
    clearInterval(intervalId);
    console.log('🏥 UI Health Monitor: Periodic checks stopped');
  };
}

/**
 * Reset health state (useful after recovery/reload)
 */
export function resetHealthState() {
  lastRenderTimestamp = Date.now();
  jsErrorCount = 0;
  lastJsError = null;
  unhandledRejectionCount = 0;
  lastUnhandledRejection = null;
  recentErrors = [];
  isUIHealthy = true;
  console.log('🏥 UI Health Monitor: State reset');
}

export default {
  markRenderSuccess,
  getTimeSinceLastRender,
  checkUIHealth,
  getUIHealthStatus,
  onHealthChange,
  initializeErrorHandlers,
  startPeriodicHealthChecks,
  resetHealthState
};
